import { el, ensureStyle } from '../dom';
import { traitsOf, traitValueLabel, TRAIT_KEYS, TRAIT_LABEL } from '../../core/traits';
import { traitIcon } from './trait-icons';
import type { ViewContext } from '../router';
import type { Species } from '../../types';
import { t } from '../../strings';

// What a bird IS, as a row of pills — one pill per axis, headed by the axis'
// name, holding every value the bird has on it.
//
// The species page and the finished Birdle board both say this, and they said
// it two different ways: a pill per value with its glyph on one, a pill per axis
// with no glyph on the other. One per axis wins because a bird that lives in
// three kinds of country is one fact with three parts, and the header is what
// tells "cup" the nest from "cup" anything else without a legend. The glyphs
// come along because they are what makes a row of these scannable.
//
// Each value is its own link into the traits guide, to that value — the pill is
// a container rather than a link, since a link can only go one place and
// "pendant" and "cup" each want their own definition.

ensureStyle(
  'trait-pills-css',
  `
  .tpills { display:flex; flex-wrap:wrap; gap:6px; }
  .tpills .chip { padding:4px 11px 4px 12px; font-size:13px; font-weight:500;
    gap:4px 9px; flex-wrap:wrap; }
  .tpills .chip .label { color:var(--ink-soft); }
  .tpills .tval { display:inline-flex; align-items:center; gap:4px;
    color:var(--ink); text-decoration:none; }
  .tpills .tval:hover { text-decoration:underline; text-underline-offset:3px; }
  .tpills .tval .tico { color:var(--ink-soft); }
`,
);

/** The five axes as pills, or null for a bird the pack has no traits for. */
export function traitPills(sp: Species, ctx: ViewContext): HTMLElement | null {
  const traits = traitsOf(sp);
  const keys = TRAIT_KEYS.filter((key) => traits[key].length);
  if (!keys.length) return null;
  return el(
    'div.tpills',
    {},
    ...keys.map((key) =>
      el(
        'div.chip',
        {},
        el('span.label', {}, TRAIT_LABEL[key]),
        ...traits[key].map((v) => {
          const path = `/traits?axis=${key}&value=${encodeURIComponent(v)}`;
          const link = el(
            'a.tval',
            { href: `#${path}`, title: t('common.traitTitle', { trait: TRAIT_LABEL[key] }) },
            traitIcon(key, v, { size: 15 }),
            traitValueLabel(key, v),
          );
          link.addEventListener('click', (e) => {
            e.preventDefault();
            ctx.navigate(path);
          });
          return link;
        }),
      ),
    ),
  );
}
